import { useEffect, useState, startTransition } from 'react';
import { useDebounce } from 'site-modules/shared/hooks/use-debounce';
import { EdmundsAPI } from 'client/data/api/api-client';
import { getMetricApiOptions } from 'client/utils/metrics-hoc';

const createResultsFetcher = () => {
  const controller = new AbortController();

  return {
    abort() {
      controller.abort();
    },
    isAborted() {
      return controller.signal.aborted;
    },
    fetchResults(query, options, extra) {
      const requestOptions = {
        method: 'POST',
        headers: {
          ...options.headers,
          'content-type': 'application/json',
        },
        body: JSON.stringify({
          question: query,
          extra,
        }),
        signal: controller.signal,
      };

      return EdmundsAPI.fetchJson('/llm/v1/hot/mmt/', requestOptions);
    },
  };
};

const DEFAULT_API_METRICS = {};

export const useGlobalSearchResults = (
  searchQuery,
  { delay = 300, apiMetrics = DEFAULT_API_METRICS, isOpen },
  visitorLocation
) => {
  const [results, setResults] = useState();
  const [delayedQuery, setDelayedQuery] = useState(searchQuery);
  const [isLoading, setIsLoading] = useState(false);

  const setDebouncedDelayedQuery = useDebounce(setDelayedQuery, delay);

  useEffect(() => {
    if (searchQuery) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- show loading immediately while the debounced query is pending.
      setIsLoading(true);
    }

    setDebouncedDelayedQuery(searchQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  useEffect(() => {
    const { abort, isAborted, fetchResults } = createResultsFetcher();

    if (delayedQuery && isOpen) {
      const fetchGlobalSearchResults = async () => {
        setIsLoading(true);
        try {
          const { zipCode, latitude, longitude } = visitorLocation;
          const extra = { zip: zipCode, loc: { lat: latitude, lon: longitude } };
          const searchResults = await fetchResults(delayedQuery, getMetricApiOptions(apiMetrics), extra);
          setResults(searchResults);
          setIsLoading(false);
        } catch {
          if (isAborted()) {
            return;
          }
          setResults(null);
          setIsLoading(false);
        }
      };

      fetchGlobalSearchResults();
    }

    if (!delayedQuery && isOpen) {
      startTransition(() => {
        setResults(undefined);
      });
    }

    return () => {
      abort?.();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delayedQuery, isOpen]);

  return { results, isLoading };
};
